import { useContext, useMemo } from "react";
import { useCompleteOrderMutation } from "@/features/organizations/api/ordersApi";
import { WebsiteContext } from "@/pages/website/WebsiteProvider";
import { useOrder } from "@/hooks";
import { getOrderDetails } from "@/utils/order";
import { Bank, Cash, Flow, Order, PaymentMethodsType } from "@/types";
import { creditCardOptions, currencyOptions, paymentMethodsLabels } from "@/constants/paymentMethods";
import { addCurrency } from "@/utils/numbers";

const useSendThroughWhatsapp = () => {
  const [completeOrder] = useCompleteOrderMutation();
  const { deleteLSOrganizationOrder } = useOrder();
  const { organization, branch } = useContext(WebsiteContext);

  const sendOrder = async (order: Order) => {

    const selecetedBranch = useMemo(() => {
      return branch?.find((b) => b.id === order.branch?.id);
    }, [branch, order.branch?.id]);

    if (!organization?.whatsapp || !order?.items?.length) return;
    const items = order.items
      .map((item: any) => {
        let dish = item.quantity + " - " + item.title;

        const settings = getOrderDetails(item);

        if (settings !== "") {
          dish = dish + "%0A" + settings;
        }

        if (item?.comments) {
          dish = dish + "%0A" + "Comentarios:" + "%0A" + item?.comments;
        }

        return dish.replace(/#/g, "%23");
      })
      .join("%0A");

    const email = order.client?.email ? `Correo:${order.client?.email}` : "";
    const drivingInstructions = `https://www.google.com/maps/dir/?api=1%26origin%3D${organization.latitude}%2C${organization.longitude}%26destination%3D${order.deliveryLocation?.latitude}%2C${order.deliveryLocation?.longitude}%26travelmode%3Ddriving`;

    const isMapAvailable = order.deliveryLocation?.latitude && order.deliveryLocation?.longitude;

    const contactInfo = `%E2%80%A2 Nombre: ${order.client?.name}%0A %E2%80%A2 Telf: ${order.client?.phone}%0A %E2%80%A2  ${email}`;
    const location = `%E2%80%A2 Dirección: ${order.deliveryLocation?.address}`;
    const paymentDetails = `%E2%80%A2 Método de Pago: ${order.paymentDetails?.method ? paymentMethodsLabels[order.paymentDetails.method] ?? "" : ""}`;
    const bankDetails =
      order.paymentDetails?.method === "BANK_TRANSFER"
        ? `%0A %E2%80%A2 Banco: ${(order.paymentDetails?.value as Bank)?.bankName} %0A %E2%80%A2 No. de Cuenta: ${(order.paymentDetails?.value as Bank)?.accountNumber} %0A %E2%80%A2 Moneda: ${currencyOptions.find((cur) => cur.value === (order.paymentDetails?.value as Bank)?.currency)?.label || (order.paymentDetails?.value as Bank)?.currency
        }`
        : order.paymentDetails?.method === PaymentMethodsType.CREDIT_CARD
          ? `%0A %E2%80%A2 Tarjeta: ${creditCardOptions.find((cur) => cur.value === order.paymentDetails?.value)?.label || order.paymentDetails?.value}`
          : order.paymentDetails?.method === PaymentMethodsType.CASH
            ? `%0A %E2%80%A2 Efectivo%0APaga%20Con%20:%20${addCurrency((order?.paymentDetails?.value as Cash).amount, "NIO")}`
            : "";

    const branchDetails = order.branch ? `%0A%0A SUCURSAL: %0A%E2%80%A2 ${order.branch?.title} %0A%E2%80%A2 ${order.branch?.location?.address}` : "";
    const scheduleTimeDetails = order.scheduleTime ? `%0A%0A HORARIO: %0A%E2%80%A2 ${new Date(order.scheduleTime).toLocaleString()}` : "";

    // create order link
    const orderLink = `https://bitelink.app/comercios/${organization.id}/orders/${order.id}`;
    const orderLinkText = `%0A%0A Ver pedido: ${orderLink}`;

    const pickupTemplate =
      orderLinkText +
      `%0A%0A TIPO: %0A "Pick Up (cliente retira en el local)"` +
      `%0A%0A PEDIDO: %0A${items}` +
      `%0A%0A CONTACTO: %0A${contactInfo}` +
      `${branchDetails}` +
      `${scheduleTimeDetails}`;

    const deliveryTemplate =
      orderLinkText +
      `%0A%0A TIPO: %0A "Delivery (cliente recibe en la dirección)"` +
      `%0A%0A PEDIDO: %0A${items}` +
      `%0A%0A CONTACTO: %0A${contactInfo}` +
      `%0A%0A DIRECCIÓN: %0A${location} ${isMapAvailable ? drivingInstructions : ""}` +
      `%0A%0A ENVIO: %0A${order?.deliveryFee ? addCurrency(order.deliveryFee, "NIO") : "A convenir"}` +
      `%0A%0A PAGO: %0A${paymentDetails} ${bankDetails}`;

    const whatsapp = order.branch?.id !== null ? selecetedBranch?.whatsapp : organization.whatsapp;

    if (order.flow === Flow.PICKUP) {
      window.open(`https://wa.me/505${whatsapp}?text=${pickupTemplate}`);
    } else {
      window.open(`https://wa.me/505${whatsapp}?text=${deliveryTemplate}`);
    }

    await completeOrder({
      order: {
        ...order,
        orderedAt: Date.now(),
        approvedAt: Date.now(),
        sendingAt: Date.now(),
        completedAt: Date.now(),
      },
      orderId: order?.id,
      organizationId: organization?.id,
    })

    if (organization?.id) deleteLSOrganizationOrder();
  };

  return sendOrder;
};

export default useSendThroughWhatsapp;
